import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Auto-focus input when visible.
 *
 * @template T
 *
 * @return {
 *   inputRef: React.RefObject<T | undefined>;
 *   AutoFocus: React.FC
 * }
 */
const useAutoFocus = <T extends { focus: () => void }>() => {
  const inputRef = useRef<T>(null);

  const AutoFocus: React.FC<React.PropsWithChildren<unknown>> = ({
    children,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const handleIntersection = useCallback(
      (entries: IntersectionObserverEntry[]) => {
        const [entry] = entries;
        if (entry.isIntersecting && inputRef.current) {
          inputRef.current.focus();
        }
      },
      []
    );

    useEffect(() => {
      const element = containerRef.current;
      if (!element) {
        return;
      }

      const observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.1,
      });
      observer.observe(element);

      return () => observer.disconnect();
    }, [handleIntersection]);

    return <div ref={containerRef}>{children}</div>;
  };

  return { AutoFocus, inputRef };
};

export default useAutoFocus;
